import { normalizeRoleName } from '../../../utils/rolePermissions'

const ROLE_LABELS: Record<string, string> = {
    owner: 'Владелец',
    admin: 'Администратор',
    lead: 'Руководитель',
    member: 'Участник',
    user: 'Пользователь',
}

function getTaskUserKey(user: any, index: number) {
    return String(
        user?.id
        || user?.user_id
        || user?.email
        || `${user?.first_name || ''}:${user?.last_name || ''}:${index}`
    )
}

function getUserIdCandidates(user: any) {
    return [
        user?.id,
        user?.user_id,
        user?.user?.id,
        user?.user?.user_id,
        user?.profile?.id,
        user?.profile?.user_id,
    ].filter(Boolean).map((value) => String(value))
}

function getUserEmailCandidates(user: any) {
    return [
        user?.email,
        user?.user?.email,
        user?.profile?.email,
    ].filter(Boolean).map((value) => String(value).toLowerCase())
}

export function getTaskCreatorIds(task: any) {
    const creatorObjects = [
        task?.created_by,
        task?.creator,
        task?.author,
        task?.owner,
    ].filter(Boolean)
    const creatorIds = [
        task?.created_by_id,
        task?.creator_id,
        task?.author_id,
        task?.owner_id,
        ...creatorObjects.flatMap((creator) => getUserIdCandidates(creator)),
    ]

    return Array.from(new Set(creatorIds.filter(Boolean).map(String)))
}

function normalizeTaskUser(user: any) {
    if (!user) {
        return null
    }

    if (typeof user === 'string') {
        return {
            name: user,
            full_name: user,
        }
    }

    const nestedUser = user.user || user.profile || {}
    const fullName = user.name || nestedUser.name || ''
    const [nameFirstPart = '', nameLastPart = ''] = fullName.trim().split(/\s+/, 2)
    const avatar = (
        user.avatar
        ?? user.photo
        ?? user.photo_data_url
        ?? user.avatar_url
        ?? user.image
        ?? nestedUser.avatar
        ?? nestedUser.photo
        ?? nestedUser.photo_data_url
        ?? nestedUser.avatar_url
        ?? nestedUser.image
        ?? null
    )

    return {
        ...nestedUser,
        ...user,
        id: user.id ?? nestedUser.id ?? user.user_id ?? nestedUser.user_id,
        user_id: user.user_id ?? nestedUser.user_id ?? user.id ?? nestedUser.id,
        email: user.email ?? nestedUser.email ?? null,
        first_name: user.first_name ?? nestedUser.first_name ?? nameFirstPart,
        last_name: user.last_name ?? nestedUser.last_name ?? nameLastPart,
        avatar,
        photo: avatar,
    }
}

export function getUserDisplayName(user: any) {
    if (!user) {
        return ''
    }

    const nestedUser = user.user || user.profile || {}
    const directName = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
    const nestedName = [nestedUser?.first_name, nestedUser?.last_name].filter(Boolean).join(' ')

    return directName
        || nestedName
        || user?.full_name
        || nestedUser?.full_name
        || user?.name
        || nestedUser?.name
        || user?.email
        || nestedUser?.email
        || ''
}

export function getUserRoleLabel(user: any) {
    if (!user) return ''

    const nestedUser = user.user || user.profile || {}
    const role = user.highest_role
        || user.role_in_workspace
        || user.role_in_project
        || user.role
        || (Array.isArray(user.roles) ? user.roles[0] : null)
        || nestedUser.highest_role
        || nestedUser.role
        || (Array.isArray(nestedUser.roles) ? nestedUser.roles[0] : null)

    const normalizedRole = normalizeRoleName(role)
    if (!normalizedRole) return ''

    if (ROLE_LABELS[normalizedRole]) {
        return ROLE_LABELS[normalizedRole]
    }

    if (typeof role === 'object') {
        return role?.title || role?.name || role?.value || normalizedRole
    }

    return String(role)
}

export function findProjectUser(
    projectUsers: any[] = [],
    options: {
        ids?: Array<string | number | null | undefined>
        emails?: Array<string | null | undefined>
    } = {},
) {
    const ids = new Set((options.ids || []).filter(Boolean).map((value) => String(value)))
    const emails = new Set((options.emails || []).filter(Boolean).map((value) => String(value).toLowerCase()))

    return projectUsers.find((user) => (
        getUserIdCandidates(user).some((candidate) => ids.has(candidate))
        || getUserEmailCandidates(user).some((candidate) => emails.has(candidate))
    )) || null
}

export function getTaskCreator(task: any, projectUsers: any[] = []) {
    const directCreator = task?.created_by || task?.creator || task?.author || task?.owner || null

    if (directCreator) {
        return normalizeTaskUser(directCreator)
    }

    return normalizeTaskUser(findProjectUser(projectUsers, {
        ids: getTaskCreatorIds(task),
        emails: [
            task?.created_by?.email,
            task?.creator?.email,
            task?.author?.email,
            task?.owner?.email,
        ],
    }))
}

export function getTaskUsers(task: any, projectUsers: any[] = []) {
    const assigneeIds = Array.isArray(task?.assignee_ids) ? task.assignee_ids : []
    const projectUsersById = new Map<string, any>()

    projectUsers.forEach((user) => {
        getUserIdCandidates(user).forEach((id) => {
            projectUsersById.set(id, user)
        })
    })

    const assigneeUsers = assigneeIds
        .map((assigneeId: string | number) => projectUsersById.get(String(assigneeId)))
        .filter(Boolean)

    const rawUsers = [
        ...(Array.isArray(task?.users) ? task.users : []),
        ...(Array.isArray(task?.assignees) ? task.assignees : []),
        ...(task?.assignee ? [task.assignee] : []),
        ...assigneeUsers,
    ]

    const seenUsers = new Set<string>()

    return rawUsers.reduce((accum: any[], currentUser: any, index: number) => {
        const normalizedUser = normalizeTaskUser(currentUser)

        if (!normalizedUser) {
            return accum
        }

        const userKey = getTaskUserKey(normalizedUser, index)

        if (seenUsers.has(userKey)) {
            return accum
        }

        seenUsers.add(userKey)
        accum.push(normalizedUser)

        return accum
    }, [])
}

function getTaskAssigneeObjects(task: any) {
    return [
        ...(Array.isArray(task?.users) ? task.users : []),
        ...(Array.isArray(task?.assignees) ? task.assignees : []),
        ...(task?.assignee ? [task.assignee] : []),
        ...(task?.responsible ? [task.responsible] : []),
        ...(task?.owner ? [task.owner] : []),
    ]
}

export function getTaskAssigneeIds(task: any) {
    const assigneeObjects = getTaskAssigneeObjects(task)
    const ids = [
        ...(Array.isArray(task?.assignee_ids) ? task.assignee_ids : []),
        task?.assigned_to,
        task?.responsible_id,
        task?.owner_id,
        task?.user_id,
        ...assigneeObjects.flatMap((assignee) => getUserIdCandidates(assignee)),
    ]

    return Array.from(new Set(ids.filter(Boolean).map((id) => String(id))))
}

export function isTaskAssignedToCurrentUser(
    task: any,
    currentUserIds: Array<string | number> = [],
    currentUserEmails: string[] = [],
) {
    const normalizedCurrentUserIds = new Set(currentUserIds.filter(Boolean).map(String))
    const normalizedCurrentUserEmails = new Set(
        currentUserEmails.filter(Boolean).map((email) => String(email).toLowerCase()),
    )

    if (normalizedCurrentUserIds.size === 0 && normalizedCurrentUserEmails.size === 0) {
        return false
    }

    if (getTaskAssigneeIds(task).some((id) => normalizedCurrentUserIds.has(id))) {
        return true
    }

    const assigneeEmails = getTaskAssigneeObjects(task).flatMap((assignee) => getUserEmailCandidates(assignee))

    return assigneeEmails.some((email) => normalizedCurrentUserEmails.has(email))
}

export function taskMatchesAssigneeFilter(task: any, selectedAssigneeIds: Array<string | number> = []) {
    if (selectedAssigneeIds.length === 0) return true

    const taskAssigneeIds = new Set(getTaskAssigneeIds(task))
    return selectedAssigneeIds.some((id) => taskAssigneeIds.has(String(id)))
}
